import { useTransactions } from 'contexts/Transactions';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ResultFilters = {
  key: string;
  value: string | null;
  label: string;
};

export const useFilters = () => {
  const { t } = useTranslation(['modalInclude', 'summary']);

  const { filter, setFilter } = useTransactions();

  const [filters, setFilters] = useState<ResultFilters[]>([]);

  const clearStatusFilter = (status: string) => {
    const statusFiltered = filter.status;

    const isFilterBoth = statusFiltered === 'both';

    const isFilterEnabled = status === 'enabled';

    const isFilterDisabled = status === 'disabled';

    if (isFilterBoth && isFilterDisabled) {
      return setFilter({ status: 'enabled' });
    }

    if (isFilterBoth && isFilterEnabled) {
      return setFilter({ status: 'disabled' });
    }

    return setFilter({ status: undefined });
  };

  const clearFilter = ({ key, value }: ResultFilters) => {
    if (key === 'created') {
      setFilter({ createdStart: undefined, createdEnd: undefined });
    }

    if (key === 'birthday') {
      setFilter({ birthStart: undefined, birthEnd: undefined });
    }

    if (key === 'status') {
      clearStatusFilter(value);
    }
  };

  useEffect(() => {
    if (!filter) return;

    const { birthEnd, birthStart, createdEnd, createdStart, status } = filter;

    let newData: ResultFilters[] = [];

    if (!status) {
      newData = newData.filter((item) => item.key !== 'status');
    } else if (status === 'both') {
      newData.push(
        { key: 'status', value: 'disabled', label: t('disabled_status') },
        { key: 'status', value: 'enabled', label: t('enabled_status') },
      );
    } else {
      newData.push({
        key: 'status',
        value: status,
        label: status === 'disabled' ? t('disabled_status') : t('enabled_status'),
      });
    }

    if (birthStart && birthEnd) {
      newData.push({
        key: 'birthday',
        value: null,
        label: `${t('birthday_date')}: ${dayjs(birthStart).format('MM/YYYY')} - ${dayjs(birthEnd).format('MM/YYYY')}`,
      });
    }

    if (createdStart && createdEnd) {
      newData.push({
        key: 'created',
        value: null,
        label: `${t('inclusion_date')}: ${dayjs(createdStart).format('MM/YYYY')} - ${dayjs(createdEnd).format(
          'MM/YYYY',
        )}`,
      });
    }

    setFilters(newData);
  }, [filter, t]);

  return { filters, clearFilter };
};
